import PageContainer from '@components/Layout/PageContainer/PageContainer';
import { IS_NATERA } from '@constants/constants';
import { DRAFT_ORDERS, MANAGE_USERS, MY_DELEGATES } from '@constants/routes';
import { DraftOrderDetailProvider } from '@contexts/useDraftOrderDetail';
import AccountReview from '@pages/account-review/AccountReview';
import DraftOrderDetail from '@pages/draftOrderDetail/DraftOrderDetail';
import DraftOrders from '@pages/draftOrders/DraftOrders';
import ErrorPage from '@pages/error/errorPage';
import Login from '@pages/login/login';
import NateraLogin from '@pages/login/nateraLogin';
import UserManagement from '@pages/manage-users/UserManagement';
import MyDelegates from '@pages/my-delegates/MyDelegates';
import { OrderMethylationReanalysis, OrderNewReport, OrderReanalysis, PatientDetail } from '@pages/patientDetail';
import RequisitionComplete from '@pages/requisitionComplete/RequisitionComplete';
import RequisitionCreate from '@pages/requisitionCreate/RequisitionCreate';
import ConsentsPublicPage from '@pages/consentsPublic/ConsentsPublicPage';
import SessionExpired from '@pages/session-expired/SessionExpired';
import Signup from '@pages/signup/Signup';
import { RequisitionsPage } from '../pages';

export type RoutePath =
  | '/requisitions'
  | '/'
  | '/login'
  | '/signup'
  | '/account-review'
  | '/error'
  | '/session-expired'
  | '/consents'
  | '/requisitions/create'
  | '/requisitions/complete'
  | '/patients/:id'
  | '/patients/:id/order-new-report/:reqId'
  | '/patients/:id/order-reanalysis/:reqId'
  | '/patients/:id/order-methylation-reanalysis/:reqId'
  | '/my-delegates'
  | '/manage-users'
  | '/draft-orders'
  | '/draft-orders/:id'
  | '*';

export interface PageRoute {
  path: RoutePath;
  element: React.ReactNode;
  PageContainer?: ({ children }: { children: React.ReactNode }) => React.ReactElement;
  oldLayout?: boolean;
  private?: boolean; // True for protected routes
  onlyAuth?: boolean; // True for auth pages
  // Do not navigate to a generic error page on API error
  preventAPIErrorRedirect?: boolean;
}

const routes: PageRoute[] = [
  {
    path: '/requisitions',
    element: <RequisitionsPage />,
    preventAPIErrorRedirect: true,
    private: true,
  },
  {
    path: '/requisitions/create',
    element: <RequisitionCreate />,
    preventAPIErrorRedirect: true,
    private: true,
  },
  {
    path: '/requisitions/complete',
    element: <RequisitionComplete />,
    private: true,
  },
  {
    path: '/patients/:id',
    element: <PatientDetail />,
    PageContainer,
    preventAPIErrorRedirect: true,
    private: true,
  },
  {
    path: '/patients/:id/order-new-report/:reqId',
    element: <OrderNewReport />,
    preventAPIErrorRedirect: true,
    private: true,
  },
  {
    path: '/patients/:id/order-reanalysis/:reqId',
    element: <OrderReanalysis />,
    preventAPIErrorRedirect: true,
    private: true,
  },
  {
    path: '/patients/:id/order-methylation-reanalysis/:reqId',
    element: <OrderMethylationReanalysis />,
    preventAPIErrorRedirect: true,
    private: true,
  },
  {
    path: '/',
    element: IS_NATERA ? <NateraLogin /> : <Login />,
    oldLayout: !IS_NATERA, // Natera login does not use the old layout
  },
  {
    path: '/login',
    element: IS_NATERA ? <NateraLogin /> : <Login />,
    onlyAuth: true,
    oldLayout: !IS_NATERA, // Natera login does not use the old layout
  },
  {
    path: '/signup',
    element: <Signup />,
    onlyAuth: true,
    preventAPIErrorRedirect: true,
  },
  {
    path: '/account-review',
    element: <AccountReview />,
    onlyAuth: true,
  },
  {
    path: MY_DELEGATES,
    element: <MyDelegates />,
    preventAPIErrorRedirect: true,
    private: true,
  },
  {
    path: MANAGE_USERS,
    element: <UserManagement />,
    preventAPIErrorRedirect: true,
    private: true,
  },
  {
    path: DRAFT_ORDERS,
    element: <DraftOrders />,
    preventAPIErrorRedirect: true,
    private: true,
  },
  {
    path: `${DRAFT_ORDERS}/:id`,
    element: (
      <DraftOrderDetailProvider>
        <DraftOrderDetail />
      </DraftOrderDetailProvider>
    ),
    preventAPIErrorRedirect: true,
    private: true,
  },
  {
    path: '/error',
    element: <ErrorPage />,
    oldLayout: true,
  },
  {
    path: '/session-expired',
    element: <SessionExpired />,
    oldLayout: true,
  },
  {
    path: '/consents',
    element: <ConsentsPublicPage />,
    oldLayout: true,
    preventAPIErrorRedirect: true,
  },
];

export default routes;
